<svelte:options accessors />

<script>
  import { _ } from "svelte-i18n";
  import { onDestroy } from "svelte";
  import { postFile } from "../../utilities/Utils.svelte";
  import { createEventDispatcher, onMount, tick } from "svelte";
  import { AzureBlobStorageService } from "../../utilities/azureBlobStorage.js";
  import {
    PHOTO_VERIFICATION_SLOW_HINT_MS,
    PHOTO_VERIFICATION_TIMEOUT_MS,
  } from "../photoVerificationOutcome.mjs";

  export let instanceId;
  export let exerciseId;
  export let exerciseDetailData;
  export let storageProvider = "azure"; // "google" or "azure"

  const dispatch = createEventDispatcher();
  
  let azureService = null;
  if (storageProvider === "azure") {
    azureService = new AzureBlobStorageService();
  }

  let state = "CAPTURE";
  let errorMsg = null;
  let attempt = 0;

  let photo1DataUrl = null;
  let video;

  // Variables to store the captured image Blobs
  let firstPhotoBlob = null;
  let stream;
  let aborter;

  export let fileNames = [];
  export let fileUrls= [];

  let isPhotoUploaded = false;
  let hasContinued = false;
  let watchdogTimer = null;
  let slowHintTimer = null;
  let showSlowHint = false;
  let loadedMetadataHandler = null;
  let canPlayHandler = null;
  let captureTimer = null;

  onMount(() => {
    aborter = new AbortController();
    dispatch("initPhotoVerification");
    startWaitTimers();
    enableCamera();
  });

  onDestroy(() => {
    console.log("the component is being destroyed");
    clearWaitTimers();
    aborter?.abort();
    stopWebcam();
  });

  // Only skipToNext hides the UI behind the wait screen, and only there does a
  // path that neither captures nor errors strand the candidate with nothing to
  // click: a camera that grants access and then never fires `canplay`, a stream
  // that stalls, an upload that hangs past its own handling. The manual flow
  // keeps its own buttons, so it needs neither timer (and a watchdog there would
  // file a `timeout` over a capture still in progress).
  //
  // Two stages. At SLOW_HINT the wait screen admits something is wrong and
  // offers a refresh — the candidate's only way out if the page is too wedged
  // for the watchdog below to run at all. At TIMEOUT the watchdog reports the
  // stall and lets the parent move on by itself.
  function startWaitTimers() {
    if (!exerciseDetailData?.skipToNext) {
      return;
    }
    if (!slowHintTimer) {
      slowHintTimer = setTimeout(() => {
        slowHintTimer = null;
        showSlowHint = true;
      }, PHOTO_VERIFICATION_SLOW_HINT_MS);
    }
    if (!watchdogTimer) {
      watchdogTimer = setTimeout(() => {
        watchdogTimer = null;
        dispatchContinueToNext(
          "timeout",
          `No photo verification result after ${PHOTO_VERIFICATION_TIMEOUT_MS}ms`,
        );
      }, PHOTO_VERIFICATION_TIMEOUT_MS);
    }
  }

  function clearWaitTimers() {
    if (watchdogTimer) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
    if (slowHintTimer) {
      clearTimeout(slowHintTimer);
      slowHintTimer = null;
    }
  }

  function reloadPage() {
    window.location.reload();
  }

  // Report how the attempt ended — success or any of the failure reasons — and
  // let the parent decide what that means. This fires regardless of skipToNext:
  // the parent auto-advances only when skipToNext is set, and otherwise just
  // records the outcome and re-enables its Submit button, so a candidate whose
  // camera failed is never left with no way forward.
  //
  // One outcome per attempt. `hasContinued` keeps a late upload resolving after
  // the watchdog (or a duplicate media event) from dispatching a second time, so
  // the parent cannot be asked to submit twice; `tryAgain` is the only thing
  // that reopens it.
  function dispatchContinueToNext(reason, error) {
    clearWaitTimers();
    if (hasContinued) {
      return;
    }
    hasContinued = true;
    console.warn("Photo verification continuing to next step:", {
      reason,
      error,
      attempt,
    });
    dispatch("continueToNext", { reason, error, attempt });
  }

  function clearVideoEventListeners() {
    if (video && loadedMetadataHandler) {
      video.removeEventListener("loadedmetadata", loadedMetadataHandler);
    }
    if (video && canPlayHandler) {
      video.removeEventListener("canplay", canPlayHandler);
    }
    loadedMetadataHandler = null;
    canPlayHandler = null;
    if (captureTimer) {
      clearTimeout(captureTimer);
      captureTimer = null;
    }
  }

  /**
   * Starts the webcam by requesting access via getUserMedia.
   * If successful, the live video stream is shown and the capture button is enabled.
   */
  async function startWebcam() {
    await tick();
    // Teardown in progress — the parent is already navigating, so there is
    // nobody left to report an outcome to.
    if (aborter?.signal.aborted) {
      console.warn("Webcam start skipped: aborted.");
      return;
    }
    if (!video || !video.isConnected) {
      console.warn("Webcam start skipped: no video element.");
      state = "ERROR";
      errorMsg = "WEBCAM_CAPTURE_ERROR";
      dispatchContinueToNext("webcam_unavailable", errorMsg);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((s) => {
        if (aborter?.signal.aborted) {
          s.getTracks().forEach((track) => track.stop());
          return;
        }

        stream = s;
        
        // Guard against null video element
        if (!video || !video.isConnected) {
          console.warn("Webcam start skipped: no video element.");
          stream.getTracks().forEach((track) => track.stop());
          state = "ERROR";
          errorMsg = "WEBCAM_CAPTURE_ERROR";
          dispatchContinueToNext("webcam_unavailable", errorMsg);
          return;
        }
        
        video.srcObject = stream;
        video.style.display = "inline-block";
        
        const captureBtnEl = document.getElementById("captureBtn");
        const enableCameraBtnEl = document.getElementById("enableCameraBtn");
        
        if (captureBtnEl) captureBtnEl.style.display = "inline-block";
        if (enableCameraBtnEl) enableCameraBtnEl.style.display = "none";

        clearVideoEventListeners();
        loadedMetadataHandler = () => {
          video.play();
        };
        video.addEventListener("loadedmetadata", loadedMetadataHandler, { once: true });

        canPlayHandler = () => {
          captureTimer = setTimeout(() => {
            captureTimer = null;
            try {
              capture();
            } catch (error) {
              state = "ERROR";
              console.error("Error capturing photo", error);
              // A translation key: errorMsg is rendered through $_().
              errorMsg = "WEBCAM_CAPTURE_ERROR";

              dispatchContinueToNext("capture_exception", errorMsg);
            }
          }, 500); // Small delay for stability
        };
        video.addEventListener("canplay", canPlayHandler, { once: true });
      })
      .catch((err) => {
        console.error("Error accessing the webcam:", err);
        state = "ERROR_WEBCAM";
        errorMsg =
          "WEBCAM_ACCESS_ERROR";

        dispatchContinueToNext("webcam_access_error", errorMsg);
      });
  }

  /**
   * Stops the webcam by stopping all tracks of the media stream.
   */
  function stopWebcam() {
    clearVideoEventListeners();
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      if (video) {
        video.srcObject = null;
      }
      console.log("Webcam stopped.");
    }
  }

  /**
   * When the user clicks the "Enable Camera" button, check for camera permissions
   * (if supported) and then request the webcam stream.
   */
  function enableCamera() {
    if (navigator.permissions) {
      // Query the camera permission state. Note: This API support varies by browser.
      navigator.permissions
        .query({ name: "camera" })
        .then((permissionStatus) => {
          console.log("Camera permission state is", permissionStatus.state);
          if (permissionStatus.state === "granted") {
            // Permission already granted, so just start the webcam.
            startWebcam();
          } else if (permissionStatus.state === "prompt") {
            // Permission not yet granted; calling getUserMedia will trigger the prompt.
            startWebcam();
          } else {
            // If the permission has been denied.
            errorMsg =
              "WEBCAM_ACCESS_ERROR";
            state = "ERROR_WEBCAM";
            dispatchContinueToNext("webcam_permission_denied", errorMsg);
          }
        })
        .catch((error) => {
          console.error("Permissions API error:", error);
          // Fallback: Directly call getUserMedia if Permissions API is not fully supported.
          startWebcam();
        });
    } else {
      // If Permissions API is not supported, simply request the webcam stream.
      startWebcam();
    }
  }

  function captureImage(callback) {
    if (!video) {
      console.error("Video element not found");
      callback(null, null);
      return;
    }
    if (video?.videoWidth === 0 || video?.videoHeight === 0) {
      console.error("Invalid video dimensions");
      callback(null, null);
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob || blob.size === 0) {
        console.error("Captured blob is invalid");
        callback(null, null);
      } else {
        const dataUrl = canvas.toDataURL("image/png");
        callback(blob, dataUrl);
      }
    }, "image/png");
  }

  function generateRandomString(length) {
    // Define the characters to choose from
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";

    for (let i = 0; i < length; i++) {
      // Pick a random index from the characters string
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }

    return result;
  }

  async function uploadToGoogle(fileName, blob) {
    let fileParams = {
      uploadType: "media",
      name: "photo-verify/" + fileName,
      key: "AIzaSyBgFT_wbOWOauPZpCWoBiRVGmgFdfHvr6o",
    };

    return await postFile(
      "https://storage.googleapis.com/upload/storage/v1/b/sensay-assessment/o",
      fileParams,
      blob,
      "image/png",
    );
  }

  async function uploadToAzure(fileName, blob) {
    if (!azureService) {
      throw new Error("Azure service not initialized");
    }

    // Create a File object from the blob with the desired filename
    const file = new File([blob], fileName, { type: "image/png" });
    
    // Upload to Azure Blob Storage in photo-verify folder
    const uploadUrl = `${azureService.containerUrl}/photo-verify/${fileName}?${azureService.sasToken}`;
    
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'image/png',
      },
      body: blob,
    });

    if (!response.ok) {
      throw new Error(`Azure upload failed: ${response.status} ${response.statusText}`);
    }

    return {
      fileName,
      url: uploadUrl.split('?')[0], // Remove SAS token for public URL
    };
  }

  async function uploadMedia() {
    let hash = generateRandomString(6);
    let fileName = `p_${instanceId}_${exerciseId}_${attempt}_${hash}.png`;

    fileNames.push(fileName);

    try {
      let uploadResult;
      
      if (storageProvider === "azure") {
        console.log("Uploading to Azure Blob Storage...");
        uploadResult = await uploadToAzure(fileName, firstPhotoBlob);
      } else {
        console.log("Uploading to Google Cloud Storage...");
        uploadResult = await uploadToGoogle(fileName, firstPhotoBlob);
      }
      
      fileUrls.push(uploadResult.url);

      console.log(`Upload successful (${storageProvider}):`, uploadResult);

      isPhotoUploaded = true;
      dispatch("photoUploaded");

      dispatchContinueToNext("photo_uploaded");
    } catch (error) {
      // Enhanced error logging for TrackJS
      console.error("Error uploading photo verification image:", {
        error: error?.message || error,
        storageProvider,
        instanceId,
        exerciseId,
        fileName,
        blobSize: firstPhotoBlob?.size,
        blobType: firstPhotoBlob?.type,
        attempt,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
      dispatchContinueToNext("photo_upload_error", error);
    }
  }

  // When the user clicks the "Take Photo" button...
  async function capture() {
    if (captureTimer) {
      clearTimeout(captureTimer);
      captureTimer = null;
    }
    attempt++;

    // Capture the first photo immediately.
    captureImage((blob, dataUrl) => {
      if (!blob || blob.size === 0) {
        console.error("Captured image is empty or invalid.");
        errorMsg = "WEBCAM_CAPTURE_ERROR";
        state = "ERROR";
        dispatchContinueToNext("capture_invalid_blob", errorMsg);
        return;
      }

      if (blob.type !== "image/png") {
        console.error("Captured image is not of type PNG.");
        errorMsg = "WEBCAM_CAPTURE_PNG_ERROR";
        state = "ERROR";
        dispatchContinueToNext("capture_invalid_type", errorMsg);
        return;
      }

      firstPhotoBlob = blob;
      photo1DataUrl = dataUrl;
      console.log("First photo captured.");

      uploadMedia();
    });

    state = "VERIFY";
  }

  function tryAgain() {
    hasContinued = false;
    errorMsg = null;
    showSlowHint = false;
    stopWebcam();
    state = "CAPTURE";
    // A retry gets its own bounded wait; without this the second attempt could
    // hang with both timers already spent.
    startWaitTimers();
    startWebcam();
  }
</script>

{#if exerciseDetailData?.skipToNext}
  <div class="row">
    <div class="col">
      {#if state === "ERROR" || state === "ERROR_WEBCAM"}
        <div class="photo-container">
          <h2>{$_("ERROR")}</h2>

          <div>
            <!-- errorMsg is a translation key, same as the visible-camera branch below. -->
            <p>{$_(errorMsg)}</p>
            <br />
            <button class="btn btn-link text-success try-again-btn" on:click={tryAgain}
              >{$_("TRY_AGAIN")}</button
            >
          </div>
        </div>
      {:else}
        <p class="wait-message">{$_("PHOTO_VERIFICATION_WAIT")}</p>
        <!-- The camera UI is hidden here, so a step that quietly stalls leaves
             nothing to click. After SLOW_HINT, hand the candidate a way out. -->
        {#if showSlowHint}
          <div class="wait-slow-hint">
            <p>{$_("PHOTO_VERIFICATION_WAIT_SLOW")}</p>
            <button class="btn btn-primary" on:click={reloadPage}
              >{$_("REFRESH_PAGE")}</button
            >
          </div>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<div class="row" class:invisible={exerciseDetailData?.skipToNext}>
  <div class="col">
    {#if state === "CAPTURE"}
      <div class="photo-container">
        <div style="text-align: center;">
          <button
            class="btn btn-primary"
            id="enableCameraBtn"
            on:click={enableCamera}>{$_("ENABLE_CAMERA")}</button
          >
          <h2 class="verification-header">{$_("PHOTO_VERIFICATION_INSTRUCTION")}</h2>
          <p class="verification-subtext">{$_("PHOTO_VERIFICATION_INSTRUCTION_SUBTEXT")}</p>
          <div class="important-message">
            <strong>{$_("IMPORTANT")}:</strong> {$_("WEBCAM_ACCESS_ERROR_ADDITIONAL_INFO")}
          </div>
          <br />
          <video
            bind:this={video}
            id="video"
            autoplay
            playsinline
            style="display: none;"
          ></video>

          <br />
          <button
            class="btn btn-primary mt-4"
            id="captureBtn"
            style="display: none;"
            on:click={capture}>{$_("TAKE_PHOTO")}</button
          >
        </div>
      </div>
    {:else if state === "VERIFY"}
      <div class="photo-container">
        <div style="text-align: center;">
          <h2 class="verification-header">{$_("PHOTO_VERIFICATION_INSTRUCTION")}</h2>
          <p class="verification-subtext">{$_("PHOTO_VERIFICATION_INSTRUCTION_SUBTEXT")}</p>
          <div class="important-message">
            <strong>{$_("IMPORTANT")}:</strong> {$_("WEBCAM_ACCESS_ERROR_ADDITIONAL_INFO")}
          </div>
          <img src={photo1DataUrl} id="photo1" alt="First captured photo" />
          <br />
          <button class="btn btn-link text-success try-again-btn" on:click={tryAgain}
            >{$_("TRY_AGAIN")}</button
          >
        </div>
      </div>
    {:else if state === "ERROR_WEBCAM"}
      <div class="photo-container">
        <h2>{$_("WEBCAM_ACCESS_ERROR_TITLE")}</h2>

        <div>
          <p>{$_("WEBCAM_ACCESS_ERROR_DESC")}</p>
          <p>{$_("WEBCAM_ACCESS_ERROR_ACTION")}</p>
          <ol>
            <li>
              {@html $_("WEBCAM_ACCESS_ERROR_STEP1", {
                values: {
                  link: `<a href="https://webcamtests.com" target="_blank" rel="noopener noreferrer">webcamtests.com</a>`,
                },
              })}
            </li>
            <li>{$_("WEBCAM_ACCESS_ERROR_STEP2")}</li>
            <li>{$_("WEBCAM_ACCESS_ERROR_STEP3")}</li>
          </ol>
          <br />
          <button class="btn btn-link text-success try-again-btn" on:click={tryAgain}
            >{$_("TRY_AGAIN")}</button>
          <p>{$_("WEBCAM_ACCESS_ERROR_ADDITIONAL_INFO")}</p>
        </div>
      </div>
    {:else if state === "ERROR"}
      <div class="photo-container">
        <h2>{$_("ERROR")}</h2>

        <div>
          <p>{$_(errorMsg)}</p>
          <br />
          <button class="btn btn-link text-success try-again-btn" on:click={tryAgain}
            >{$_("TRY_AGAIN")}</button
          >
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .invisible {
    display: none;
  }

  video,
  img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }
  .photo-container {
    margin-top: 20px;
  }

  .try-again-btn {
    margin-top: 0.25rem;
    font-weight: bold;
  }

  .important-message {
    display: block;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
    background-color: #fff7e6;
    border: 1px solid #ffd36a;
    color: #b36b00;
    padding: 0.5rem 0.75rem;
    border-radius: 999px;
    font-size: 0.95rem;
    margin-top: 0.75rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .verification-header {
    font-size: 1.55rem;
    font-weight: 800;
    color: #10243c;
    margin-top: 0.4rem;
    margin-bottom: 0.6rem;
    text-align: center;
  }

  .wait-message {
    text-align: center;
    font-size: 1.25rem;
    line-height: 1.5;
    font-weight: 500;
    color: #10243c;
    margin: 1rem 0;
  }

  .wait-slow-hint {
    max-width: 34rem;
    margin: 0 auto;
    padding: 0.85rem 1rem 1rem;
    text-align: center;
    background-color: #fff7e6;
    border: 1px solid #ffd36a;
    border-radius: 12px;
    color: #b36b00;
  }

  .wait-slow-hint p {
    margin-bottom: 0.75rem;
    line-height: 1.5;
  }

  :global(.verification-subtext) {
    text-align: center !important;
  }
</style>
